/**
 * PWAInstallProvider — single source of truth for the install experience.
 *
 * Wraps the app tree once, exposes `useInstallState()`, and owns the
 * bottom sheet. Two entry points feed into it:
 *
 *   1. First-mobile-visit auto-open — surfaces the sheet ~400 ms after
 *      load so first-time users discover installability.
 *   2. Header "Install app" chip (see InstallChip.jsx) — the persistent
 *      affordance every subsequent visit.
 *
 * Rendering rules (identical for both entry points):
 *   • Mobile viewport only.
 *   • Hidden if the app is already installed (`display-mode: standalone`
 *     or `navigator.standalone`).
 *   • Dismissal remembered in localStorage for 30 days.
 *   • On Android / Chromium, tapping the CTA fires the native
 *     `beforeinstallprompt.prompt()`.
 *   • On iOS, tapping opens the visual Add-to-Home-Screen guide.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { X, Share, PlusSquare, Download, Sparkles } from "lucide-react";

const DISMISS_KEY = "cc:pwa-install-dismissed";
const DISMISS_TTL_DAYS = 30;

/* ---------- capability detection ---------- */

function isStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  if (window.navigator.standalone === true) return true;
  return false;
}

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  const narrow = window.matchMedia?.("(max-width: 820px)").matches;
  const touch =
    "ontouchstart" in window ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
  return !!(narrow && touch);
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iPadDesktop =
    /Macintosh/.test(ua) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || iPadDesktop;
}

function isInAppBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /FBAN|FBAV|Instagram|Line|MicroMessenger|Twitter|LinkedInApp/.test(ua);
}

function readDismiss() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    const ageDays = (Date.now() - ts) / 86400000;
    return ageDays < DISMISS_TTL_DAYS;
  } catch {
    return false;
  }
}

function writeDismiss() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* private mode / storage disabled — nothing to persist */
  }
}

/* ---------- context ---------- */

const InstallCtx = createContext({
  canInstall: false,
  isInstalled: false,
  isMobile: false,
  promptInstall: () => {},
});

export function useInstallState() {
  return useContext(InstallCtx);
}

export function PWAInstallProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Boot: read capabilities once client-side.
  useEffect(() => {
    if (typeof window === "undefined") return;
    setInstalled(isStandalone());
    setIsMobile(isMobileViewport());
    setIsIos(isIOS() && !isInAppBrowser());
  }, []);

  // Long-lived listeners.
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onBIP = (e) => {
      // Keep the deferred event so we can fire it from the chip.
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      setSheetOpen(false);
      writeDismiss();
    };
    const onResize = () => setIsMobile(isMobileViewport());
    const onPageShow = () => {
      setInstalled(isStandalone());
      setIsMobile(isMobileViewport());
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        setInstalled(isStandalone());
      }
    };

    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // First-mobile-visit auto-open (unchanged behaviour). Runs after boot
  // effect populates `installed` + `isMobile`.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (installed || !isMobile) return;
    if (readDismiss()) return;
    // Only auto-open on iOS — Chromium/Android already has a native mini
    // infobar plus our chip; two prompts would feel pushy.
    if (!isIos) return;
    const t = setTimeout(() => setSheetOpen(true), 400);
    return () => clearTimeout(t);
  }, [installed, isMobile, isIos]);

  const mode = deferredPrompt ? "native" : isIos ? "ios" : null;
  const canInstall = !installed && isMobile && mode !== null;

  const closeSheet = useCallback(() => {
    setSheetOpen(false);
    writeDismiss();
  }, []);

  const promptInstall = useCallback(async () => {
    // Android / Chromium: fire the native prompt directly. The event
    // MUST be consumed inside a user-gesture handler, which this is.
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        await deferredPrompt.userChoice;
      } catch {
        /* user cancelled — no-op */
      }
      setDeferredPrompt(null);
      setSheetOpen(false);
      writeDismiss();
      return;
    }
    // iOS: no programmatic install — surface the guide.
    setSheetOpen(true);
  }, [deferredPrompt]);

  const ctxValue = useMemo(
    () => ({
      canInstall,
      isInstalled: installed,
      isMobile,
      promptInstall,
    }),
    [canInstall, installed, isMobile, promptInstall],
  );

  return (
    <InstallCtx.Provider value={ctxValue}>
      {children}
      {sheetOpen && mode && (
        <InstallSheet
          mode={mode}
          onDismiss={closeSheet}
          onNativeInstall={promptInstall}
        />
      )}
    </InstallCtx.Provider>
  );
}

/* ---------- sheet ---------- */

function InstallSheet({ mode, onDismiss, onNativeInstall }) {
  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed inset-x-0 bottom-0 z-[9999] md:hidden pointer-events-none"
      style={{
        paddingBottom: "max(env(safe-area-inset-bottom, 0px), 8px)",
      }}
      aria-live="polite"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to top, rgba(4,9,20,0.35) 0%, rgba(4,9,20,0) 55%)",
        }}
        aria-hidden="true"
      />

      <div className="relative px-3 pb-3 pt-2 pointer-events-auto">
        <div
          className="mx-auto w-full max-w-md rounded-2xl border border-white/10 shadow-[0_20px_60px_-10px_rgba(4,9,20,0.55)] overflow-hidden"
          style={{
            background:
              "linear-gradient(180deg, #0F1A2E 0%, #0B1220 60%, #050B18 100%)",
            animation: "pwa-slide-up 380ms cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          <div className="flex items-start gap-3 p-4 pb-3">
            <div className="shrink-0">
              <div
                className="h-12 w-12 rounded-xl border border-[#D4A65A]/30 flex items-center justify-center overflow-hidden"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(212,166,90,0.16) 0%, rgba(212,166,90,0.04) 100%)",
                }}
              >
                <img
                  src="/logo192.png"
                  alt=""
                  width={40}
                  height={40}
                  className="h-10 w-10 object-contain"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#F0D497]">
                <Sparkles size={11} />
                Install
              </div>
              <div className="mt-0.5 font-display text-[16px] leading-snug text-white font-semibold tracking-tight">
                Keep CanniComply on your home screen
              </div>
              <div className="mt-0.5 text-[12.5px] text-slate-300 leading-relaxed">
                {mode === "native"
                  ? "One tap. Launches full screen, no browser bar. Works exactly like a native app."
                  : "Add CanniComply to your Home Screen for one-tap access — full screen, no browser bar."}
              </div>
            </div>

            <button
              type="button"
              onClick={onDismiss}
              data-testid="pwa-install-dismiss"
              aria-label="Dismiss install prompt"
              className="shrink-0 -mt-1 -mr-1 h-8 w-8 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06] inline-flex items-center justify-center transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {mode === "native" ? (
            <div className="px-4 pb-4 pt-1 flex items-center gap-2">
              <button
                type="button"
                onClick={onDismiss}
                data-testid="pwa-install-later"
                className="flex-1 rounded-xl border border-white/[0.08] text-slate-300 hover:text-white hover:bg-white/[0.05] text-[13px] font-semibold py-2.5 transition-colors"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={onNativeInstall}
                data-testid="pwa-install-cta"
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#D4A65A] hover:bg-[#c99647] text-[#0B1220] text-[13px] font-semibold py-2.5 shadow-[0_6px_18px_-6px_rgba(212,166,90,0.55)] transition-colors"
              >
                <Download size={14} />
                Install
              </button>
            </div>
          ) : (
            <IOSInstallSteps />
          )}
        </div>
      </div>

      <style>{`
        @keyframes pwa-slide-up {
          0%   { transform: translateY(24px); opacity: 0; }
          100% { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/* ---------- iOS Add-to-Home-Screen guide ---------- */

function IOSInstallSteps() {
  return (
    <div className="px-4 pb-4 pt-1">
      <ol
        className="mt-2 rounded-xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.06] overflow-hidden"
        data-testid="pwa-install-ios-steps"
      >
        <IOSStep n={1} icon={<Share size={16} strokeWidth={1.6} />}>
          Tap the <b className="text-white font-semibold">Share</b> icon in
          Safari's toolbar.
        </IOSStep>
        <IOSStep n={2} icon={<PlusSquare size={16} strokeWidth={1.6} />}>
          Choose <b className="text-white font-semibold">Add to Home Screen</b>.
        </IOSStep>
        <IOSStep
          n={3}
          icon={
            <img
              src="/logo192.png"
              alt=""
              width={20}
              height={20}
              className="h-5 w-5 object-contain rounded"
            />
          }
        >
          Tap <b className="text-white font-semibold">Add</b> — CanniComply
          lands on your Home Screen.
        </IOSStep>
      </ol>

      <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-[#D4A65A]" />
          Safari on iPhone / iPad
        </span>
        <span>Takes about 5 seconds</span>
      </div>
    </div>
  );
}

function IOSStep({ n, icon, children }) {
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <span
        className="shrink-0 h-7 w-7 rounded-lg flex items-center justify-center text-[#F0D497]"
        style={{
          background:
            "linear-gradient(180deg, rgba(212,166,90,0.14) 0%, rgba(212,166,90,0.04) 100%)",
          border: "1px solid rgba(212,166,90,0.25)",
        }}
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className="text-[9.5px] font-mono uppercase tracking-[0.18em] text-slate-500 tabular-nums w-4">
        0{n}
      </span>
      <span className="text-[12.5px] text-slate-200 leading-snug">
        {children}
      </span>
    </li>
  );
}

/* Default export preserves the old import path
   `import InstallPrompt from "@/components/pwa/InstallPrompt"`. */
export default PWAInstallProvider;
